import type { FC } from 'react';
import { Check, EyeOff, Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { smsTextsInputHint } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm/shared/sms-texts-over-length.utils';
import { cn } from '@/lib/utils';

export { TextPickerItem } from './TextPickerItem';

export const CreativeSearchInput: FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <div className="relative mb-2">
    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
    <input
      className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-xs"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export const CreativeAddRow: FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onAdd: () => Promise<void>;
  /** Disables the Add button when empty or busy */
  disabled: boolean;
  /** Disables the input (e.g. during API call). Omit to only disable button when empty. */
  inputDisabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  flex1?: boolean;
  /** Use textarea for JSON/multi-line paste (senders, texts) */
  multiline?: boolean;
  /**
   * Enables the SMS hint under the input and sets the segment limit it reports.
   * A pasted JSON array / multi-line block is judged text by text, not as one blob.
   */
  smsSegmentLengthLimit?: number;
}> = ({
  value,
  onChange,
  placeholder,
  onAdd,
  disabled,
  inputDisabled,
  onKeyDown,
  flex1,
  multiline,
  smsSegmentLengthLimit,
}) => {
  const smsHint =
    smsSegmentLengthLimit != null ? smsTextsInputHint(value, smsSegmentLengthLimit) : null;
  const overSms = smsHint !== null;
  return (
    <div className="mb-2 space-y-1">
      <div className={cn('flex gap-2', flex1 && 'flex-1', multiline && 'items-start')}>
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={cn(
              'min-h-[72px] min-w-0 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-xs outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              overSms && 'border-destructive/80 ring-1 ring-destructive/25',
            )}
            disabled={inputDisabled ?? false}
            onKeyDown={
              onKeyDown ?? ((e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), onAdd()))
            }
          />
        ) : (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={cn('h-8 text-xs', flex1 && 'min-w-0 flex-1', overSms && 'border-destructive')}
            disabled={inputDisabled ?? false}
            onKeyDown={onKeyDown ?? ((e) => e.key === 'Enter' && (e.preventDefault(), onAdd()))}
          />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1 text-xs"
          onClick={onAdd}
          disabled={disabled}
        >
          <Plus className="h-3 w-3" /> Add
        </Button>
      </div>
      {smsHint && (
        <p className="text-[10px] font-medium text-destructive" role="status">
          {smsHint}
        </p>
      )}
    </div>
  );
};

export const SenderPickerItem: FC<{
  isSelected: boolean;
  onToggle: () => void;
  label: string;
  pendingHide?: boolean;
  replacedByAi?: boolean;
}> = ({ isSelected, onToggle, label, pendingHide, replacedByAi }) => (
  <div
    role="button"
    tabIndex={0}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onToggle}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
    className={cn(
      'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 transition-colors',
      isSelected && 'bg-primary/10 text-primary',
      !isSelected && replacedByAi && 'bg-muted/30 text-muted-foreground',
      !isSelected && !replacedByAi && 'hover:bg-muted/50',
    )}
  >
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors',
        isSelected ? 'border-primary bg-primary' : 'border-input bg-background',
      )}
    >
      {isSelected && <Check className="h-2.5 w-2.5 stroke-[2.5] text-primary-foreground" />}
    </span>
    <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
    {replacedByAi && !pendingHide && (
      <span className="shrink-0 text-[10px] text-muted-foreground">Previous</span>
    )}
    {pendingHide && (
      <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] text-amber-600">
        <EyeOff className="h-2.5 w-2.5" /> Hides
      </span>
    )}
  </div>
);
